import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CreativeUnitPackageListingService } from '../creative-unit-package-listing.service';
import { PublicCreativeUnitPackageListing } from '../../../../../../../api/src/creative-unit-package-listing/creative-unit-package-listing.entity';
import { GlobalService } from '../../global';
import { MatDialog } from '@angular/material/dialog';
import { CreativeUnitPackageListingChildrenDialogComponent } from '../creative-unit-package-listing-children-dialog/creative-unit-package-listing-children-dialog.component';
import { CreativeUnitPackageService } from '../../ad/ad-unit-package.service';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { QueryParams } from '../../collection/collection.model';
import { FormGroup, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { SelectedPartialListingEvent } from '../../../../pages/project/project.page';
import { LocaleDto } from "../../../../../../../api/src/creative-unit-package/models/locale.dto";

@Component({
	selector: 'app-creative-unit-package-listing-container',
	templateUrl: './creative-unit-package-listing-container.component.html',
	styleUrls: ['./creative-unit-package-listing-container.component.scss']
})
export class CreativeUnitPackageListingContainerComponent implements OnInit, OnDestroy {
	readonly defaultSearchLabel = $localize `:Default Search Label@@listingContainerDefaultSearchLabel:Search`;
	readonly defaultSearchPlaceholder = $localize `:Default Search Placeholder@@listingContainerDefaultSearchPlaceholder:Search listings...`;
	readonly defaultPageSizeOptions: number[] = [5, 10, 20, 25, 30];
	readonly defaultPageSize = 20;

	@Input() filters: { [key: string]: any };
	@Input() layout = 'grid-4';
	@Input() thumbnailStyle = 'contain';

	@Input() searchbar: { enabled: boolean; placeholder?: string, label?: string } = {
		enabled: false,
		placeholder: this.defaultSearchPlaceholder,
		label: this.defaultSearchLabel,
	};

	@Input() paginator: { enabled: boolean; pageSize: number; pageSizeOptions: number[] } = {
		enabled: true,
		pageSize: this.defaultPageSize,
		pageSizeOptions: this.defaultPageSizeOptions
	};
	@Input() locale: LocaleDto;

	@Input() useTemplates: boolean = false; // If true, use the templates from the creative unit package service.
	@Input() businessIds: string[]; // If provided, filter listings by these business IDs.
	@Input() preloadedListings: PublicCreativeUnitPackageListing[]; // If provided, use these instead of making initial API call

	@Output() selectedListing: EventEmitter<PublicCreativeUnitPackageListing> = new EventEmitter<PublicCreativeUnitPackageListing>();
	@Output() selectedPartialListing: EventEmitter<SelectedPartialListingEvent> = new EventEmitter<SelectedPartialListingEvent>();

	public form: FormGroup;

	public totalResults: number = 0;
	public pageSize = this.defaultPageSize;

	public creativeUnitPackageListings: PublicCreativeUnitPackageListing[];
	public showPaginator: boolean = true;
	private destroy$ = new Subject<void>();

	constructor(
		private readonly creativeUnitPackageService: CreativeUnitPackageService,
		private readonly creativeUnitPackageListingService: CreativeUnitPackageListingService,
		private readonly globalService: GlobalService,
		private readonly dialog: MatDialog
	) {}

	ngOnInit(): void {
		if (!this.paginator) {
			this.paginator = {
				enabled: true,
				pageSize: this.defaultPageSize,
				pageSizeOptions: this.defaultPageSizeOptions
			};
		}

		/*if we delete a pagesize out of options(that was set to default) and we don't select new default value,
		we need this, otherwise it can occur that it hides pagination bar, and doesn't load all data*/
		if (!this.paginator?.pageSizeOptions?.includes(this.paginator?.pageSize)) {
			this.pageSize = this.paginator?.pageSizeOptions?.[0] || this.defaultPageSize;
		} else {
			this.pageSize = this.paginator?.pageSize || this.paginator?.pageSizeOptions?.[0] || this.defaultPageSize;
		}

		this.form = new UntypedFormGroup({
			search: new UntypedFormControl('')
		});

		this.form.get('search').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(search => {
			this.loadData(1, this.pageSize, search);
		});

		// Use preloaded data if available, otherwise fetch from API
		if (this.preloadedListings && this.preloadedListings.length > 0) {
			this.creativeUnitPackageListings = this.preloadedListings;
			this.totalResults = this.preloadedListings.length;
			const smallestPaginationSelectionOptions = Math.min(...(this.paginator?.pageSizeOptions || this.defaultPageSizeOptions));
			this.showPaginator = this.totalResults > smallestPaginationSelectionOptions;
		} else {
			this.loadData(1, this.pageSize, this.getSearchValue());
		}
	}

	async onSelectedListing(listing: PublicCreativeUnitPackageListing) {
		if (listing.children?.length > 0) {
			let dialogRef = this.dialog.open(CreativeUnitPackageListingChildrenDialogComponent, {
				data: {
					listing: listing,
				},
				panelClass: ['max-width-md']
			});

			let result = await dialogRef.afterClosed().toPromise();

			if (result) {
				this.selectedListing.emit(result);
			}
		} else {
			this.selectedListing.emit(listing);
		}
	}

	paginationChange(page = 1, limit: number) {
		this.pageSize = limit;
		this.loadData(page, limit, this.getSearchValue());
	}

	loadData(page = 1, limit = this.defaultPageSize, search: string) {
		let params: QueryParams = {
			page,
			limit,
			sortBy: {
				id: 'name',
				direction: 'DESC'
			},
			filters: { ...this.filters }
		};

		if (search) {
			params.query = search;
			params.filters.includeUnitsInQuery = true;
			params.filters.includeUnits = true;
		}
		// If filterByProjectBusiness is true, add businessIds to filters if available
		if (this.businessIds && this.businessIds.length > 0) {
			params.filters.businessIds = this.businessIds;
		}
		// If you need to inject business/project context, do it here

		if (this.businessIds && this.businessIds.length > 0) {
			params.businessIds = this.businessIds;
		}

		let method: Observable<{ totalResults: number; data: PublicCreativeUnitPackageListing[] }> = this.creativeUnitPackageListingService.get(params);

		if (this.useTemplates) {
			method = this.creativeUnitPackageService.findSummaries({
				...params,
				onlyPublished: true, // Only fetch published templates
			})
				.pipe(
					// Convert the response to the expected type.
					map(response => {
						return {
							totalResults: response.totalResults,
							data: response.results as any as PublicCreativeUnitPackageListing[]
						};
					})
				);
		}

		method.subscribe(
			response => {
				this.totalResults = response.totalResults;
				this.creativeUnitPackageListings = response.data;

				const pageSizeOptions = this.paginator?.pageSizeOptions || this.defaultPageSizeOptions;

				const smallestPaginationSelectionOptions = Math.min(...pageSizeOptions);
				this.showPaginator = response.totalResults > smallestPaginationSelectionOptions;
				let creativeUnitPackageListings = response.data;

				// If we got filters.ids, then resort to the order of the ids.
				if (this.filters?.ids) {
					creativeUnitPackageListings = creativeUnitPackageListings.sort((a, b) => {
						return this.filters.ids.indexOf(a.id) - this.filters.ids.indexOf(b.id);
					});
				}

				this.creativeUnitPackageListings = creativeUnitPackageListings;
			},
			err => this.globalService.triggerErrorMessage(err)
		);
	}

	public getSearchValue(): string {
		return this.form.get('search').value;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
